import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const REQUIRED_PHRASE = "WIPE ALL DATA";

function DeleteClientSubsection() {
  const [clients, setClients] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/clients");
      setClients(r.data || []);
    } catch (e) {
      console.error("clients load failed:", e);
    }
  };
  useEffect(() => { load(); }, []);

  const selectedClient = clients.find((c) => c.id === selectedId);
  const phraseMatches = !!selectedClient && typed === selectedClient.name;

  const closeDialog = () => {
    setOpen(false);
    setTyped("");
  };

  const confirmDelete = async () => {
    if (!selectedClient || !phraseMatches) return;
    setBusy(true);
    try {
      await api.delete(`/clients/${selectedClient.id}`);
      toast.success(`${selectedClient.name} deleted`);
      setSelectedId("");
      closeDialog();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-red-200 bg-red-50/30 rounded-sm p-4" data-testid="danger-delete-client">
      <div className="text-sm font-medium text-[#0f172a] mb-1">Delete a client</div>
      <p className="text-xs text-slate-600 leading-relaxed max-w-2xl mb-3">
        Permanently removes a client along with all of its projects, tasks, pages, and compliance
        databases. Attendance, leave records, and other workspace data are not affected. This
        cannot be undone.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={selectedId} onValueChange={setSelectedId}>
          <SelectTrigger className="w-full sm:w-80 rounded-sm bg-white" data-testid="select-client-to-delete">
            <SelectValue placeholder={`Select a client${clients.length ? ` (${clients.length})` : ""}`} />
          </SelectTrigger>
          <SelectContent>
            {clients.length === 0 && <div className="px-2 py-3 text-xs text-slate-400">No clients</div>}
            {[...clients].sort((a, b) => a.name.localeCompare(b.name)).map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          className="rounded-sm border-red-300 text-red-700 hover:bg-red-100 disabled:opacity-50"
          disabled={!selectedClient}
          onClick={() => setOpen(true)}
          data-testid="btn-open-delete-client"
        >
          Delete this client…
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeDialog())}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="dialog-delete-client">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700" style={{ fontFamily: "Outfit" }}>
              <AlertTriangle size={18} /> Delete client
            </DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                You are about to delete{" "}
                <span className="font-semibold text-[#0a2540]">{selectedClient.name}</span>{" "}
                and <span className="font-medium">all of its projects, tasks, pages, and compliance databases</span>.
                This action is irreversible.
              </p>
              <div>
                <Label className="text-xs">
                  Type the client name{" "}
                  <span className="font-mono-data text-red-700">{selectedClient.name}</span>{" "}
                  to confirm:
                </Label>
                <Input
                  className="mt-1 rounded-sm font-mono-data"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={selectedClient.name}
                  autoFocus
                  data-testid="input-delete-client-confirm"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={closeDialog} disabled={busy} data-testid="btn-delete-client-cancel">
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
              onClick={confirmDelete}
              disabled={!phraseMatches || busy}
              data-testid="btn-delete-client-confirm"
            >
              {busy ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Deleting…</> : "Delete client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WipeAllSubsection() {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/wipe-data", { confirm: phrase });
      setResult(r.data);
      toast.success("Data wiped — your account is preserved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    setPhrase("");
    setResult(null);
    if (result) {
      window.location.href = "/dashboard";
    }
  };

  return (
    <div className="border border-red-200 bg-red-50/30 rounded-sm p-4" data-testid="danger-wipe-all">
      <div className="text-sm font-medium text-[#0f172a] mb-1">Wipe all data</div>
      <p className="text-xs text-slate-600 leading-relaxed max-w-2xl">
        Removes every client, project, task, page, compliance database, holiday, attendance
        record, leave request, notification, announcement, audit log, invite, comment, resource,
        file (except your branding logo), and every other user from this workspace.{" "}
        <span className="font-medium text-red-700">Your admin account, settings, and branding are preserved.</span>
        {" "}This is intended for one-time cleanup after evaluation/testing. The action cannot be undone.
      </p>
      <Button
        variant="outline"
        className="mt-4 rounded-sm border-red-300 text-red-700 hover:bg-red-100"
        onClick={() => setOpen(true)}
        data-testid="btn-open-wipe-data"
      >
        Wipe all data…
      </Button>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="dialog-wipe-data">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700" style={{ fontFamily: "Outfit" }}>
              <AlertTriangle size={18} /> Wipe all workspace data
            </DialogTitle>
          </DialogHeader>

          {!result ? (
            <>
              <div className="space-y-3">
                <p className="text-sm text-slate-700">
                  This will permanently delete <span className="font-medium">all clients, projects, tasks,
                  pages, databases, holidays, attendance, leaves, notifications, announcements, audit logs,
                  files, and every other user</span> from this workspace.
                </p>
                <p className="text-sm text-slate-700">
                  Your admin account, your Pulse branding (logo + name), working days, attendance rules, and
                  configured external tools will be preserved.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900 leading-relaxed">
                  This action is irreversible. We strongly recommend you take a manual database backup before
                  proceeding if you might want this data later.
                </div>
                <div>
                  <Label className="text-xs">
                    Type <span className="font-mono-data text-red-700">{REQUIRED_PHRASE}</span> to confirm:
                  </Label>
                  <Input
                    className="mt-1 rounded-sm font-mono-data"
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    placeholder={REQUIRED_PHRASE}
                    data-testid="input-wipe-confirm"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={close} disabled={busy} data-testid="btn-wipe-cancel">
                  Cancel
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
                  onClick={confirm}
                  disabled={phrase !== REQUIRED_PHRASE || busy}
                  data-testid="btn-wipe-confirm"
                >
                  {busy ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Wiping…</> : "I understand — Wipe everything"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-3">
                <p className="text-sm text-slate-700 font-medium">Wipe complete.</p>
                <div className="text-xs font-mono-data bg-slate-50 border border-slate-200 rounded-sm p-3 max-h-64 overflow-y-auto" data-testid="wipe-result">
                  {Object.entries(result.deleted || {})
                    .filter(([, n]) => n > 0)
                    .sort((a, b) => b[1] - a[1])
                    .map(([k, n]) => (
                      <div key={k}>
                        {k}: <span className="text-red-700">−{n}</span>
                      </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">Preserved admin: <span className="font-mono-data">{result.preserved_admin}</span></p>
              </div>
              <DialogFooter>
                <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={close} data-testid="btn-wipe-done">
                  Reload dashboard
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function DangerZoneSection() {
  return (
    <section data-testid="section-danger-zone">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle size={14} className="text-red-600" />
        <h2 className="text-sm font-semibold text-red-700" style={{ fontFamily: "Outfit" }}>Danger Zone</h2>
      </div>
      <div className="space-y-3">
        <DeleteClientSubsection />
        <WipeAllSubsection />
      </div>
    </section>
  );
}
